import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AuthenticationService } from '@app/auth';
import { BillingService } from '@app/core/services/billing/billing/billing.service';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, Subject, debounceTime } from 'rxjs';
import { BillingPriceDTO, WithdrawalProductType } from 'src/generated/generatedEntities';
import { RegisterService } from './register.service';
import { BillingInformationFormComponent } from './billing-information-form/billing-information-form.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { I18nModule } from '@app/i18n';
import { PasswordStrengthMeterComponent } from 'angular-password-strength-meter';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { RegistrationModel } from './models/registrationModel';
import { TextInputComponent } from '@app/calculators/components/text-input/text-input.component';
import { AppValidationMessageDirective } from '@app/calculators/effort-planner-form/form-validation/validationMessage';
import { DynamicSingleSelecterComponent } from '@app/calculators/components/dynamic-single-selecter/dynamic-single-selecter.component';
import { HttpErrorResponse } from '@angular/common/http';

interface BillingModule {
  type: WithdrawalProductType;
  label: string;
  inBundle: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-register-form',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    I18nModule,
    PasswordStrengthMeterComponent,
    BillingInformationFormComponent,
    MatCheckboxModule,
    MatOptionModule,
    MatSelectModule,
    MatCardModule,
    MatButtonModule,
    TextInputComponent,
    AsyncPipe,
    AppValidationMessageDirective,
    DynamicSingleSelecterComponent,
  ],
  templateUrl: './register-form.component.html',
  styleUrls: ['./register-form.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['prices', 'global', 'register', 'settings', 'billing', 'error'],
    },
  ],
})
export class RegisterFormComponent {
  private authenticationService = inject(AuthenticationService);
  private billingService = inject(BillingService);
  private registerService = inject(RegisterService);
  private billingFetchSubject = new Subject<void>();

  private static readonly MAX_EMPLOYEES_FOR_SELF_SERVICE = 50;
  // Error keys that are returned from the backend :)
  private static readonly ERROR_KEYS = {
    userExists: 'login already in use',
    emailExists: 'email address already in use',
    organisationExists: 'organisation name already in use',
    invalidEmail: 'invalid email',
  };

  readonly registrationModel = new RegistrationModel(undefined);
  readonly maxEmployeesForSelfService = RegisterFormComponent.MAX_EMPLOYEES_FOR_SELF_SERVICE;
  readonly billingModules: BillingModule[] = [
    { type: 'LCC_PACKAGE', label: 'register.form.module.lcc_bundle', inBundle: false }, // The bundle package itself
    { type: 'BUILDING_COSTS_AND_DATES', label: 'register.form.module.building-costs', inBundle: true },
    { type: 'OPERATING_COSTS', label: 'register.form.module.operating-costs', inBundle: true },
    { type: 'REBUILD_COSTS', label: 'register.form.module.rebuild-costs', inBundle: true },
    { type: 'LCC', label: 'register.form.module.lcc', inBundle: true },
    { type: 'LCA', label: 'register.form.module.lca', inBundle: false }, // NOT part of bundle
    { type: 'EFFORT_PLANNER', label: 'register.form.module.effort-planner', inBundle: false },
  ] as const;
  readonly LCC_BUNDLE_TYPES: WithdrawalProductType[] = [
    'BUILDING_COSTS_AND_DATES',
    'OPERATING_COSTS',
    'REBUILD_COSTS',
    'LCC',
  ] as const;

  readonly isLoading = signal(false);
  readonly hasStartFreeMonth = signal(false);
  readonly checkSuccess = signal(false);
  readonly contactSuccess = signal(false);
  readonly billingResponse = signal({} as BillingPriceDTO);
  readonly registerComplete = signal(false);
  readonly numEmp = signal(1);
  readonly message = signal('');
  readonly formDisabled = signal(false);
  readonly hasAcceptedNoAnonymous = signal(false);
  readonly hasAcceptedConditions = signal(false);
  readonly submitted = signal(false);
  readonly hasError = signal<string | undefined>(undefined);
  readonly effortPlannerChecked = signal(false);
  readonly selectedWithdrawalProductTypes = signal<WithdrawalProductType[]>([]);

  readonly showPriceTable = computed(() => {
    return this.selectedWithdrawalProductTypes().length > 0 || this.effortPlannerChecked();
  });

  constructor() {
    this.billingFetchSubject.pipe(debounceTime(300), untilDestroyed(this)).subscribe(() => {
      this.fetchBilling();
    });
  }

  readonly isLCCBundleSelected = computed(() => {
    return this.selectedWithdrawalProductTypes().includes('LCC_PACKAGE');
  });

  onBillingModuleChange(event: MatCheckboxChange, moduleType: WithdrawalProductType) {
    if (moduleType === 'LCC_PACKAGE') {
      if (event.checked) {
        this.selectedWithdrawalProductTypes.set([
          ...this.selectedWithdrawalProductTypes().filter(
            (t) => !this.LCC_BUNDLE_TYPES.includes(t) && t !== 'LCC_PACKAGE',
          ),
          'LCC_PACKAGE',
        ]);
      } else {
        this.selectedWithdrawalProductTypes.set(
          this.selectedWithdrawalProductTypes().filter((t) => t !== 'LCC_PACKAGE'),
        );
      }
    } else if (moduleType === 'EFFORT_PLANNER') {
      /* NOTE: EFFORT_PLANNER is offered for free until the end of 2025
        it is tracked separately and not added to the selectedWithdrawalProductTypes
       */
      this.effortPlannerChecked.set(event.checked);
    } else {
      if (event.checked) {
        if (!this.selectedWithdrawalProductTypes().includes(moduleType)) {
          this.selectedWithdrawalProductTypes.set([...this.selectedWithdrawalProductTypes(), moduleType]);
        }
      } else {
        this.selectedWithdrawalProductTypes.set(this.selectedWithdrawalProductTypes().filter((t) => t !== moduleType));
      }
    }
    this.billingFetchSubject.next();
  }

  isModuleChecked(moduleType: WithdrawalProductType): boolean {
    if (moduleType === 'EFFORT_PLANNER') {
      return this.effortPlannerChecked();
    }
    return (
      (this.isLCCBundleSelected() && this.LCC_BUNDLE_TYPES.includes(moduleType)) ||
      this.selectedWithdrawalProductTypes().includes(moduleType)
    );
  }

  isModuleDisabled(module: BillingModule): boolean {
    // LCC is only available as part of the bundle
    if (module.type === 'LCC') {
      return true;
    }
    if (module.type === 'LCC_PACKAGE') {
      // Bundle can always be toggled
      return false;
    }
    if (module.inBundle) {
      // Bundle modules disabled if bundle is selected
      return this.isLCCBundleSelected();
    }
    // LCA (stand-alone) is never disabled
    return false;
  }

  private toRegisterDto(
    model: RegistrationModel,
    withdrawalProductTypes: WithdrawalProductType[],
    login: string | undefined,
    message: string | undefined,
  ) {
    const dto = model.toDto();
    dto.account.login = login ?? dto.account.email;
    dto.organisation.withdrawalProductTypes = withdrawalProductTypes;
    dto.message = message;
    return dto;
  }

  checkRegistration() {
    this.resetErrors();
    this.registrationModel.resetValidations();
    this.registrationModel.validate();

    this.submitted.set(true);
    if (!this.registrationModel.isValid() || !this.hasAcceptedConditions() || !this.hasAcceptedNoAnonymous()) {
      return;
    }

    const registerObj = this.toRegisterDto(
      this.registrationModel,
      this.selectedWithdrawalProductTypes(),
      undefined,
      undefined,
    );

    this.numEmp.set(registerObj.organisation.numEmployees);

    const regData$ = this.authenticationService.checkRegDetails(registerObj);
    regData$.pipe(untilDestroyed(this)).subscribe({
      next: (data) => {
        if (data === 'OK') {
          this.checkSuccess.set(true);
          this.hasStartFreeMonth.set(true);
          this.formDisabled.set(true);
        }
      },
      error: (e) => {
        this.handleError(e);
      },
    });
  }

  fetchBilling(): void {
    this.resetErrors();

    this.isLoading.set(true);

    const registerObj = this.toRegisterDto(
      this.registrationModel,
      this.selectedWithdrawalProductTypes(),
      this.registrationModel.account.email,
      undefined,
    );

    this.billingService
      .fetchTentativePrice(registerObj)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (res: BillingPriceDTO) => {
          this.billingResponse.set(res);
        },
        error: (e) => {
          this.handleError(e);
        },
      });
  }

  //create account
  register() {
    const registerObj = this.toRegisterDto(
      this.registrationModel,
      this.selectedWithdrawalProductTypes(),
      this.registrationModel.account.email,
      undefined,
    );

    const regData$ = this.authenticationService.createAccount(registerObj);
    regData$.pipe(untilDestroyed(this)).subscribe({
      next: (res) => {
        if (res === 'SUCCESS') {
          this.registerComplete.set(true);
          this.hasStartFreeMonth.set(true);
        }
      },
      error: (e) => {
        this.handleError(e);
      },
    });
  }

  // contact Reg if employee > 51
  sendContact() {
    const registerObj = this.toRegisterDto(
      this.registrationModel,
      this.selectedWithdrawalProductTypes(),
      this.registrationModel.account.email,
      this.message(),
    );

    const regData$ = this.registerService.contactUs(registerObj);
    regData$.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.contactSuccess.set(true);
      },
      error: (e) => {
        this.contactSuccess.set(false);
        this.handleError(e);
      },
    });
  }

  changeSiaStatus(type: 'individual' | 'corporate', status: boolean) {
    if (type === 'individual') {
      this.registrationModel.organisation.siaIndividualMember = status;
      if (status) {
        this.registrationModel.organisation.siaCorporateMember = false;
      }
    } else {
      this.registrationModel.organisation.siaCorporateMember = status;
      if (status) {
        this.registrationModel.organisation.siaIndividualMember = false;
      }
    }
  }

  resetErrors() {
    this.hasError.set(undefined);
  }

  parseReferenceToNumber(e: string): number {
    const cleaned = e.replaceAll(/[^0-9]/g, '');
    const parsed = parseInt(cleaned, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  handleError(e: HttpErrorResponse) {
    if (e.status === 400 && typeof e.error === 'string') {
      switch (e.error) {
        case RegisterFormComponent.ERROR_KEYS.userExists:
          this.hasError.set('register.messages.error.userexists');
          break;
        case RegisterFormComponent.ERROR_KEYS.emailExists:
          this.hasError.set('register.messages.error.emailexists');
          break;
        case RegisterFormComponent.ERROR_KEYS.organisationExists:
          this.hasError.set('register.messages.error.orgnameexists');
          break;
        case RegisterFormComponent.ERROR_KEYS.invalidEmail:
          this.hasError.set('register.messages.error.invalidemail');
          break;
        default:
          this.hasError.set('error.unknown-error');
          break;
      }
    } else {
      this.hasError.set('error.unknown-error');
    }
  }
}
